import { useMemo, useState, useEffect } from "react";
import { Calculator, Send, Sparkles, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { localize, termDiscount, priceExVat, vatPrice } from "@/data/vehicles";
import { useVatMode } from "@/lib/vatMode";
import InquiryModal from "@/components/InquiryModal";
import { useLang } from "@/i18n/LanguageContext";
import { WhatsAppIcon } from "@/components/BrandIcons";
import { useVehicles } from "@/context/VehiclesContext";

// 3 500 km / month is included for free; anything above is charged per km.
const INCLUDED_KM = 3500;
const EXTRA_KM_PRICE = 3; // CZK per km over the included limit
const KM_OPTIONS = [3000, 3500, 4000, 5000];
const kmSurcharge = (km) => Math.max(0, km - INCLUDED_KM) * EXTRA_KM_PRICE;

const fmt = (n) => Number(n).toLocaleString("cs-CZ");

const monthsLabel = (m, t) => {
  if (m === 1) return `1 ${t("calc.monthUnit1")}`;
  if (m >= 2 && m <= 4) return `${m} ${t("calc.monthsUnit234")}`;
  return `${m} ${t("calc.monthsUnit")}`;
};

export const PriceCalculator = () => {
  const { lang, t } = useLang();
  const vat = useVatMode();
  const { vehicles } = useVehicles();
  const CALC_VEHICLES = useMemo(() => vehicles.filter((v) => v.priceMonthly != null), [vehicles]);
  const [slug, setSlug] = useState("");
  const [months, setMonths] = useState(6);
  const [kmValue, setKmValue] = useState(3500);
  const [showInquiry, setShowInquiry] = useState(false);

  useEffect(() => {
    if (!slug && CALC_VEHICLES.length) setSlug(CALC_VEHICLES[0].slug);
  }, [CALC_VEHICLES, slug]);

  const vehicle = useMemo(() => CALC_VEHICLES.find((v) => v.slug === slug), [CALC_VEHICLES, slug]);
  const discount = termDiscount(months);

  const monthly = vehicle
    ? Math.round((vehicle.priceMonthly + kmSurcharge(kmValue)) * (1 - discount))
    : 0;
  const total = monthly * months;

  const kmLabel = `${fmt(kmValue)} km`;
  const waText = vehicle
    ? encodeURIComponent(
        `${t("calc.waMessage")}: ${vehicle.name}, ${monthsLabel(months, t)}, ${kmLabel}/${t("calc.monthUnit1")}. ${t("calc.resultMonthly")}: ${fmt(monthly)} Kč/${t("calc.monthUnit1")}. ${t("calc.waSuffix")}`
      )
    : "";
  const waUrl = `https://wa.me/420725706235?text=${waText}`;

  return (
    <section
      id="calculator"
      className="py-16 sm:py-24 px-4 sm:px-6 relative"
      data-testid="calculator-section"
    >
      <div className="gradient-radial absolute inset-0 pointer-events-none" />
      <div className="max-w-5xl mx-auto relative">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t("calc.eyebrow")}</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">{t("calc.title")}</h2>
          <p className="text-zinc-500 max-w-2xl mx-auto mt-3 sm:mt-4 text-sm sm:text-base">
            {t("calc.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-6">
          {/* Controls */}
          <div className="lg:col-span-3 glass p-5 sm:p-8 rounded-2xl space-y-7">
            {/* Vehicle */}
            <div>
              <label className="block text-xs sm:text-sm text-zinc-400 mb-2">{t("calc.vehicle")}</label>
              <Select value={slug} onValueChange={setSlug}>
                <SelectTrigger className="input-dark text-base h-auto py-3" data-testid="calc-vehicle-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-zinc-200 max-h-72">
                  {CALC_VEHICLES.map((v) => (
                    <SelectItem key={v.slug} value={v.slug} data-testid={`calc-vehicle-${v.slug}`}>
                      {v.name} — {fmt(v.priceMonthly)} Kč/{t("calc.monthUnit1")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Months */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs sm:text-sm text-zinc-400">{t("calc.months")}</label>
                <span className="text-orange-400 font-bold text-sm sm:text-base" data-testid="calc-months-value">
                  {monthsLabel(months, t)}
                </span>
              </div>
              <Slider
                value={[months]}
                min={1}
                max={24}
                step={1}
                onValueChange={(v) => setMonths(v[0])}
                data-testid="calc-months-slider"
              />
              <div className="flex justify-between text-[11px] text-zinc-400 mt-2">
                <span>1</span>
                <span>24 {t("calc.monthsUnit")}</span>
              </div>
            </div>

            {/* Km limit */}
            <div>
              <label className="block text-xs sm:text-sm text-zinc-400 mb-2.5">{t("calc.km")}</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {KM_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setKmValue(opt)}
                    className={`py-2.5 px-1 rounded-lg border text-xs sm:text-sm font-medium transition-colors ${
                      kmValue === opt
                        ? "border-orange-500 bg-orange-500/15 text-white"
                        : "border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/20"
                    }`}
                    data-testid={`calc-km-${opt}`}
                  >
                    {fmt(opt)}
                  </button>
                ))}
              </div>
              <p className="text-zinc-400 text-[11px] mt-2.5">{t("calc.extraKm")}</p>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-2 glass p-5 sm:p-8 rounded-2xl border-orange-500/30 flex flex-col">
            <p className="text-xs uppercase tracking-wider text-zinc-500">{t("calc.resultMonthly")}</p>
            {vehicle?.priceOriginal > vehicle?.priceMonthly && (
              <p className="text-zinc-500 line-through text-sm mt-1" data-testid="calc-original-monthly">
                {fmt(Math.round((vehicle.priceOriginal + kmSurcharge(kmValue)) * (1 - discount)))} Kč
              </p>
            )}
            <p className="text-orange-400 font-extrabold text-3xl sm:text-4xl mt-1" data-testid="calc-result-monthly">
              {fmt(vatPrice(monthly, vat).main)} Kč
            </p>
            <p className="text-zinc-500 text-sm mt-0.5">/ {t("calc.monthUnit1")} {vatPrice(monthly, vat).mainLabel}</p>
            <p className="text-zinc-400 text-xs mt-0.5" data-testid="calc-result-monthly-exvat">{fmt(vatPrice(monthly, vat).sub)} Kč {vatPrice(monthly, vat).subLabel}</p>

            {discount > 0 && (
              <div className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-medium mt-3 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1.5 w-fit">
                <Sparkles className="w-3.5 h-3.5" />
                −{Math.round(discount * 100)} % {t("calc.discountNote")}
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-white/10">
              <p className="text-xs uppercase tracking-wider text-zinc-500">{t("calc.resultTotal")}</p>
              <p className="text-white font-bold text-xl sm:text-2xl mt-1" data-testid="calc-result-total">
                {fmt(vatPrice(total, vat).main)} Kč
              </p>
              <p className="text-zinc-500 text-[11px] mt-0.5">{fmt(vatPrice(total, vat).sub)} Kč {vatPrice(total, vat).subLabel}</p>
              <p className="text-zinc-500 text-xs mt-0.5">
                {monthsLabel(months, t)} · {fmt(kmValue)} km / {t("calc.monthUnit1")}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowInquiry(true)}
              className="btn-accent w-full inline-flex items-center justify-center gap-2 mt-6"
              data-testid="calc-inquiry-btn"
            >
              <Send className="w-4 h-4" /> {t("calc.cta")}
            </button>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 mt-3 py-3 px-5 rounded-full font-semibold text-white bg-[#25D366] hover:bg-[#1fb957] transition-colors"
              data-testid="calc-whatsapp-btn"
            >
              <WhatsAppIcon className="w-[18px] h-[18px]" /> {t("calc.whatsapp")}
            </a>
            <p className="text-zinc-500 text-[11px] text-center mt-3">{t("calc.note")}</p>

            {/* What's included */}
            <div className="mt-5 pt-5 border-t border-white/10" data-testid="calc-included">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2.5">{t("calc.included")}</p>
              <ul className="space-y-1.5">
                {["calc.inc1", "calc.inc2", "calc.inc3", "calc.inc4"].map((k) => (
                  <li key={k} className="flex items-center gap-2 text-zinc-300 text-xs sm:text-sm">
                    <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    {t(k)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {vehicle && (
        <InquiryModal
          vehicleName={`${vehicle.name} · ${monthsLabel(months, t)} · ${fmt(kmValue)} km (${fmt(monthly)} Kč/${t("calc.monthUnit1")})`}
          open={showInquiry}
          onClose={() => setShowInquiry(false)}
        />
      )}
    </section>
  );
};

export default PriceCalculator;
